import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Flex } from '@chakra-ui/react';
import PropTypes from 'prop-types';

import Authorizer, { FeatureFlags } from '../../../../../components/Authorizer';
import RichTextEditor from '../../../../../components/RichTextEditor';
import { EmailAutomationTriggers } from '../../../../../constants/emailAutomations';
import { createToast } from '../../../../../redux/modules/app';
import {
  selectCheckboxMap, selectCurrentMessageHtml,
  selectCurrentSubjectHtml, selectDefaultTemplate,
  setCurrentMessageHtml, setCurrentSubjectHtml
} from '../../../../../redux/modules/emails';
import EmailTemplateDropdown from '../../EmailTemplateDropdown';
import PersonalizeDropdown from '../../PersonalizeDropdown';
import SelectedCodesList from '../../SelectedCodesList';

const EmailBody = ({
  subjectContainerId,
  bodyContainerId,
  selectCodeType,
  onBodyChange,
  onSubjectChange,
  bodyValue,
  subjectValue,
  automationTrigger,
  disableTemplateCreation,
  defaultTemplates,
  ...rest
}) => {
  const dispatch = useDispatch();
  const subjectEditorRef = useRef();
  const contentEditorRef = useRef();
  const selectedCodes = useSelector(selectCheckboxMap);
  const currentMessageHtml = useSelector(selectCurrentMessageHtml);
  const currentSubjectHtml = useSelector(selectCurrentSubjectHtml);
  const defaultTemplate = useSelector(selectDefaultTemplate);

  const { templateInfo: { subjectHtml: defaultSubjectHtml, html: defaultBodyHtml } } = defaultTemplate || { templateInfo: {} };

  useEffect(() => {
    if (!currentMessageHtml && !onBodyChange) {
      dispatch(setCurrentMessageHtml(defaultBodyHtml));
    }

    if (!currentSubjectHtml && !onSubjectChange) {
      dispatch(setCurrentSubjectHtml(defaultSubjectHtml));
    }

    if (onBodyChange && currentMessageHtml) {
      onBodyChange(currentMessageHtml);
    }

    if (onSubjectChange && currentSubjectHtml) {
      onSubjectChange(currentSubjectHtml);
    }
  }, [currentMessageHtml, currentSubjectHtml]);

  const handleBodyChange = val => {
    if (onBodyChange) {
      onBodyChange(val);
      return;
    }

    dispatch(setCurrentMessageHtml(val));
  };

  const handleSubjectchange = val => {
    if (onSubjectChange) {
      onSubjectChange(val);
      return;
    }

    dispatch(setCurrentSubjectHtml(val));
  };

  return (
    <Box p={4} {...rest}>
      <Flex justifyContent="flex-start">
        <EmailTemplateDropdown
          defaultTemplates={defaultTemplates}
          disableTemplateCreation={disableTemplateCreation}
          templateWord="Template"
          type="main_content"
        />
        <PersonalizeDropdown
          automationTrigger={automationTrigger}
          px={2}
          onClick={val => {
            navigator.clipboard.writeText(val);

            dispatch(createToast({
              title: 'Token Copied',
              type: 'info',
              duration: 3000
            }));
          }}
        />
        <Authorizer featureFlag={[FeatureFlags.Codes, FeatureFlags.Items]}>
          <SelectedCodesList
            addCodeType={selectCodeType}
            codeMap={selectedCodes}
          />
        </Authorizer>
      </Flex>
      <Box fontWeight="semibold" marginRight="auto" marginTop={8} textAlign="left">Subject: </Box>
      <Flex alignItems="center" height="50px" justifyContent="flex-start">
        <RichTextEditor
          id={subjectContainerId}
          innerRef={subjectEditorRef}
          value={subjectValue !== undefined ? subjectValue : currentSubjectHtml || defaultSubjectHtml}
          onChange={handleSubjectchange}
        />
      </Flex>
      <Box fontWeight="semibold" marginTop={4} textAlign="left">Message: </Box>
      <Box bg="white" mt="2px">
        <RichTextEditor
          showToolbar
          id={bodyContainerId}
          innerRef={contentEditorRef}
          value={bodyValue !== undefined ? bodyValue : currentMessageHtml || defaultBodyHtml}
          onChange={handleBodyChange}
        />
      </Box>
    </Box>
  );
};

EmailBody.propTypes = {
  subjectContainerId: PropTypes.string.isRequired,
  bodyContainerId: PropTypes.string.isRequired,
  selectCodeType: PropTypes.oneOf(['modal', 'popover']).isRequired,
  onBodyChange: PropTypes.func,
  onSubjectChange: PropTypes.func,
  bodyValue: PropTypes.string,
  subjectValue: PropTypes.string,
  automationTrigger: PropTypes.oneOf(Object.values(EmailAutomationTriggers)),
  disableTemplateCreation: PropTypes.bool,
  defaultTemplates: PropTypes.arrayOf(PropTypes.shape({}))
};

export default EmailBody;
